import { displayModeConfigAtom } from '@repo/shared/state';
import { JOTAI_STORE } from '@repo/shared/state/core/jotai';
import { sentryAddBreadcrumb } from '@repo/shared/utils/sentry';

type LogLevel = keyof Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>;

// `error` and `warn` only. A single failed request already logs three times, and the breadcrumb
// trail holds 100 entries — adding `info`/`log`/`debug` would push the useful ones out.
const SENTRY_BREADCRUMB_LOG_LEVELS: LogLevel[] = ['warn', 'error'];

const logGenerator = ({ logLevel }: { logLevel: LogLevel; }) => {
	return async (...messages: unknown[]) => {
		try {
			// eslint-disable-next-line no-console
			console?.[logLevel]?.(...messages);
		} catch {
			// Do nothing
		}
		try {
			if (SENTRY_BREADCRUMB_LOG_LEVELS?.indexOf?.(logLevel) === -1 || JOTAI_STORE.get(displayModeConfigAtom)?.isEmbeddedWidgetMode !== true) {
				return;
			}
			// String arguments, plus any `Error` reduced to its name and message. Other objects logged
			// beside them are request/response dumps the request breadcrumb already carries in
			// structured form, and they can be large or hold user text. Errors are the exception: in
			// `logger.error('RF Error Boundary Component Error Debug: ', error)` the error *is* the
			// payload, and dropping it left the breadcrumb as a bare label with nothing after it. The
			// stack is deliberately not included — the event itself already carries it.
			const message = messages?.map?.((entry) => (typeof entry === 'string') ? entry : ((entry instanceof Error) ? `${entry?.name ?? 'Error'}: ${entry?.message ?? ''}` : ''))?.filter?.((entry) => typeof entry === 'string' && entry?.length > 0)?.join?.(' ');
			if (typeof message !== 'string' || message?.length === 0) {
				return;
			}
			await sentryAddBreadcrumb({ category: 'console', level: (logLevel === 'warn') ? 'warning' : 'error', message });
		} catch {
			// Do nothing
		}
	};
};

export const logger = {
	log: logGenerator({ logLevel: 'log' }),
	info: logGenerator({ logLevel: 'info' }),
	warn: logGenerator({ logLevel: 'warn' }),
	error: logGenerator({ logLevel: 'error' }),
	debug: logGenerator({ logLevel: 'debug' })
};
